import React, { useState, useRef, useEffect } from 'react';
import * as _ from 'lodash';
import moment from 'moment';
import { DatePicker, Radio } from '@td-client-frontend/common';
import { CheckboxModal } from './CheckboxModal';
import { chronicCareCode, DATE_FORMAT } from '../../constants/formConstants';
import { getFieldsKeys, getCode, checkStateConditionsForSpp, getCcmLabel } from '../../utils/groups/helpers';

interface Props {
  values: any;
  initialState: any;
  onFieldChange: (name: string, value: any) => any;
  onListChange: (list: any) => any;
  baseFieldName: string;
}
const options = [
  { value: 'PLUS', label: 'Plus' },
  { value: 'STANDALONE', label: 'Standalone' }
];

// render helpers
export const firstLineProducts = ['DIAB', 'DIABP', 'HYPT', 'SPP'];
const secondLineProducts = ['MYSTRPLUS', 'WEIGM'];

export function FuturePlanUpdateCCM({ values, initialState, onFieldChange, baseFieldName, onListChange }: Props) {
  const [fields, setFields] = useState(_.get(values, baseFieldName));
  const originalValues = initialState.groups[baseFieldName.substring(7, 8)];

  const getDateValue = (date: string | null, isEnabled: boolean, productStartDate: string) => {
    if (isEnabled) {
      return date;
    } else if (!isEnabled && productStartDate) {
      return productStartDate;
    } else if (!isEnabled && !productStartDate) {
      return date;
    } else {
      return null;
    }
  };

  useEffect(() => {
    // handles spp update when the products it depends on are updated
    const enableSpp = checkStateConditionsForSpp(fields, true);
    if (!enableSpp && fields[`futureProductsCCM--SPP--isEnabled`]) {
      const updatedFields = { ...fields };
      updatedFields[`futureProductsCCM--SPP--end_dt`] = fields['futureProductsCCM--CCM--start_dt'];
      updatedFields[`futureProductsCCM--SPP--isEnabled`] = false;
      updatedFields[`futureProductsCCM--SPP--start_dt`] =
        fields[`futureProductsCCM--SPP--start_dt`] !== originalValues[`futureProductsCCM--SPP--start_dt`]
          ? originalValues[`futureProductsCCM--SPP--start_dt`]
          : fields[`futureProductsCCM--SPP--start_dt`];
      onListChange(updatedFields);
      setFields(updatedFields);
    }
  }, [fields, onListChange, originalValues]);

  // Render products as they are on form state when first open the modal
  const initialKeys = getFieldsKeys(fields, 'futureProductsCCM');
  if (initialKeys.length === 0) {
    const fieldsKeys = getFieldsKeys(fields, 'isEnabled');
    fieldsKeys.forEach((key) => {
      const code = getCode(key);
      if (chronicCareCode.includes(code) && fields[key]) {
        fields[`futureProductsCCM--${code}--isEnabled`] = fields[`products--${code}--isEnabled`];
        fields[`futureProductsCCM--${code}--start_dt`] = null;
        fields[`futureProductsCCM--${code}--end_dt`] = null;
        if (code !== 'SPP') fields[`futureProductsCCM--${code}--bundleType`] = fields[`products--${code}--bundleType`];
        return fields;
      }
    });
  }

  // Updates date for all future products when selected
  const updateFutureProductsDates = (date: string | null) => {
    const updateStartDate = (code: string) => {
      const isEnabled = fields[`futureProductsCCM--${code}--isEnabled`];
      if (!fields[`futureProductsCCM--${code}--end_dt`]) {
        fields[`futureProductsCCM--${code}--start_dt`] = getDateValue(
          date,
          isEnabled,
          fields[`futureProductsCCM--${code}--start_dt`]
        );
        onFieldChange(
          `futureProductsCCM--${code}--start_dt`,
          getDateValue(date, isEnabled, fields[`futureProductsCCM--${code}--start_dt`])
        );
      }
    };

    const updateEndDate = (code: string) => {
      const isEnabled = fields[`futureProductsCCM--${code}--isEnabled`];
      const startDate = fields[`futureProductsCCM--${code}--start_dt`];
      const endDate = fields[`futureProductsCCM--${code}--end_dt`];
      // we update the end date as future date selected and maintain the start date as it is
      // (we don't want start date to be shown as updated in activity log)
      if (!isEnabled && endDate) {
        fields[`futureProductsCCM--${code}--end_dt`] = date;
        onFieldChange(`futureProductsCCM--${code}--end_dt`, date);
      }
      // Special handling for service disabled before selecting a date
      else if (!isEnabled && !startDate) {
        fields[`futureProductsCCM--${code}--end_dt`] = endDate || date;
        onFieldChange(`futureProductsCCM--${code}--end_dt`, endDate || date);
      }
    };
    const fieldKeys = getFieldsKeys(fields, 'futureProductsCCM');
    fieldKeys.forEach((key) => {
      const code = getCode(key);
      if (fields[`futureProductsCCM--${code}--isEnabled`]) {
        updateStartDate(code);
      } else {
        updateEndDate(code);
      }
    });
  };

  // ==== HANDLERS ==== //

  // Handles date updates
  const handleOnSelectDate = (value: Date) => {
    const date = value.toISOString() ? moment(value.toISOString()).format(DATE_FORMAT) : null;
    updateFutureProductsDates(date);
    onHandleChangeList('futureProductsCCM--CCM--start_dt', date);
  };

  // Handles updates in checkboxes
  const onHandleListUpdate = (checked: boolean, product: string) => {
    const updatedFields = { ...fields };
    if (checked) {
      updatedFields[`futureProductsCCM--${product}--isEnabled`] = true;
      updatedFields[`futureProductsCCM--${product}--start_dt`] =
        updatedFields['futureProductsCCM--CCM--start_dt'] ?? null;
      updatedFields[`futureProductsCCM--${product}--end_dt`] = null;
      if (product !== 'SPP')
        updatedFields[`futureProductsCCM--${product}--bundleType`] = fields[`products--${product}--bundleType`];
      onListChange(updatedFields);
      setFields(updatedFields);
    } else {
      updatedFields[`futureProductsCCM--${product}--isEnabled`] = false;
      updatedFields[`futureProductsCCM--${product}--end_dt`] = fields['futureProductsCCM--CCM--start_dt'];
      updatedFields[`futureProductsCCM--${product}--start_dt`] =
        fields[`futureProductsCCM--${product}--start_dt`] !== originalValues[`futureProductsCCM--${product}--start_dt`]
          ? originalValues[`futureProductsCCM--${product}--start_dt`]
          : fields[`futureProductsCCM--${product}--start_dt`];
      onListChange(updatedFields);
      setFields(updatedFields);
    }
  };

  // Handles bundletype updates (radio component)
  const onHandleChangeList = (name: string, value: any) => {
    onFieldChange(name, value);
    setFields({
      ...fields,
      [name]: value
    });
  };

  const renderCheckboxes = (products: string[]) =>
    products.map((product: string, i) => {
      let tooltip;
      let isDisabled = false;
      if (product === 'SPP') {
        tooltip = 'All Whole Person Programs included in one enrollment';
        isDisabled = !checkStateConditionsForSpp(fields, true);
      }
      const checkboxLabel = getCcmLabel(product);
      const checkboxKey = `${checkboxLabel} ${i}`;

      return (
        <div key={checkboxKey}>
          <CheckboxModal
            checked={fields[`futureProductsCCM--${product}--isEnabled`]}
            name={`futureProductsCCM--${product}--start_dt`}
            className="m-1"
            onHandleChangeList={onHandleListUpdate}
            tooltip={tooltip}
            label={checkboxLabel}
            disabled={isDisabled}
            product={product}
          />
        </div>
      );
    });

  // use this ref for set width when radio buttons disappear
  const widthRef = useRef(null);
  const renderRadioButtons = (products: string[]) => {
    return products.map((product) => {
      const width = widthRef.current?.offsetWidth ?? '153.23';
      if (product !== 'SPP' && fields[`futureProductsCCM--${product}--isEnabled`]) {
        return (
          <div ref={widthRef} key={`futureProductsCCM--${product}--bundleType`}>
            <Radio
              initialValue={fields[`futureProductsCCM--${product}--bundleType`]}
              label=""
              name={`futureProductsCCM--${product}--bundleType`}
              onChange={(value: string | boolean) => {
                onHandleChangeList(`futureProductsCCM--${product}--bundleType`, value);
              }}
              options={options}
            />
          </div>
        );
      } else if (product === 'SPP') {
        return null;
      } else {
        return <div key={`futureProductsCCM--${product}--bundleType`} style={{ width: `${width}px` || '' }}></div>;
      }
    });
  };

  return (
    <section className="w-full overflow-y-auto p-6 pt-3 font-bold">
      <div className="w-full">
        <h3 className="font-medium pt-0 pb-3">Chronic Care Services</h3>
        <div className="grid gap-1" style={{ gridTemplateColumns: '1fr 1fr 1fr 1fr' }}>
          <div className="mt-auto col-span-5 col-end-2">
            <DatePicker
              name="futureProductsCCM--CCM--start_dt"
              label="Update date"
              onChange={(value: Date) => {
                handleOnSelectDate(value);
              }}
              selected={
                _.get(fields, 'futureProductsCCM--CCM--start_dt')
                  ? moment(_.get(fields, 'futureProductsCCM--CCM--start_dt')).toDate()
                  : null
              }
            />
          </div>
        </div>

        <div className="grid gap-2" style={{ gridTemplateColumns: '1fr 1fr 1fr 1fr' }}>
          {renderCheckboxes(firstLineProducts)}
        </div>

        <div className="grid gap-2 font-normal text-sm" style={{ gridTemplateColumns: '1fr 1fr 1fr 1fr' }}>
          {renderRadioButtons(firstLineProducts)}
        </div>
        <div className="grid gap-2 font-normal" style={{ gridTemplateColumns: '1fr 1fr 1fr 1fr' }}>
          {renderCheckboxes(secondLineProducts)}
        </div>
      </div>
    </section>
  );
}
